// Unified create slide-over. 540px right panel with a coloured 4px
// accent strip across the top, a title bar (entity-tinted icon tile +
// "Create · New {kind}" + close), a 4-pill type switcher row with
// keyboard-shortcut chips (R / M / T / O), then the kind-specific
// form body and split footer.
//
// Keyboard: R / M / T / O switches kind unless focus is in an input /
// textarea / contentEditable. Esc closes.

import { useEffect } from "react";
import { CheckSquare, Flag, Lightbulb, Target, X } from "lucide-react";
import type { CreateKind, CreateSeed } from "./createSlideOver/types";
import { DC, ENTITY } from "./createSlideOver/primitives";
import { RockForm } from "./createSlideOver/RockForm";
import { MilestoneForm } from "./createSlideOver/MilestoneForm";
import { TodoForm } from "./createSlideOver/TodoForm";
import { OpportunityForm } from "./createSlideOver/OpportunityForm";

interface PlannerCreateSlideOverProps {
  open: boolean;
  kind: CreateKind;
  seed?: CreateSeed;
  onClose: () => void;
  onKindChange: (kind: CreateKind) => void;
  onCreated: () => void;
}

const META: Record<
  CreateKind,
  {
    label: string;
    color: string;
    tint: string;
    shortcut: "R" | "M" | "T" | "O";
    icon: React.ReactNode;
    title: string;
  }
> = {
  rock: {
    label: "Rock",
    color: ENTITY.rock.color,
    tint: ENTITY.rock.tint,
    shortcut: "R",
    icon: <Target size={18} />,
    title: "New Rock",
  },
  milestone: {
    label: "Milestone",
    color: ENTITY.milestone.color,
    tint: ENTITY.milestone.tint,
    shortcut: "M",
    icon: <Flag size={18} />,
    title: "New Milestone",
  },
  todo: {
    label: "To-do",
    color: ENTITY.todo.color,
    tint: ENTITY.todo.tint,
    shortcut: "T",
    icon: <CheckSquare size={18} />,
    title: "New To-do",
  },
  opportunity: {
    label: "Opportunity",
    color: ENTITY.opportunity.color,
    tint: ENTITY.opportunity.tint,
    shortcut: "O",
    icon: <Lightbulb size={18} />,
    title: "New Opportunity",
  },
};

function isInTextEntry(el: HTMLElement | null): boolean {
  if (!el) return false;
  const tag = el.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  if (el.isContentEditable) return true;
  if (typeof el.closest === "function") {
    if (el.closest('[contenteditable="true"], [contenteditable=""]')) return true;
  }
  return false;
}

export function PlannerCreateSlideOver({
  open,
  kind,
  seed,
  onClose,
  onKindChange,
  onCreated,
}: PlannerCreateSlideOverProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const target = e.target as HTMLElement | null;
      const active = document.activeElement as HTMLElement | null;
      if (isInTextEntry(target) || isInTextEntry(active)) return;
      const k = e.key.toLowerCase();
      if (k === "r") {
        e.preventDefault();
        onKindChange("rock");
      } else if (k === "m") {
        e.preventDefault();
        onKindChange("milestone");
      } else if (k === "t") {
        e.preventDefault();
        onKindChange("todo");
      } else if (k === "o") {
        e.preventDefault();
        onKindChange("opportunity");
      } else if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, onKindChange]);

  if (!open) return null;
  const meta = META[kind];

  return (
    <>
      <div className="tp-sheet-backdrop" onClick={onClose} aria-hidden />
      <aside
        className="tp-sheet"
        role="dialog"
        aria-modal="true"
        style={{ width: "min(540px, 96vw)" }}
      >
        {/* 4px accent strip — entity tint */}
        <div style={{ height: 4, background: meta.color, flex: "0 0 4px" }} />

        <header className="tp-sheet-header" style={{ paddingBottom: 14 }}>
          <div
            aria-hidden
            style={{
              width: 36,
              height: 36,
              borderRadius: 8,
              background: meta.tint,
              color: meta.color,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {meta.icon}
          </div>
          <div style={{ flex: 1 }}>
            <div
              style={{
                font: "700 10px/1 var(--bsp-font-sans)",
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: DC.grey,
                marginBottom: 3,
              }}
            >
              Create
            </div>
            <div
              style={{
                font: "700 18px/1.1 var(--bsp-font-display)",
                letterSpacing: "0.02em",
                color: DC.dark,
                textTransform: "uppercase",
              }}
            >
              {meta.title}
            </div>
          </div>
          <button
            type="button"
            className="tp-icon-action"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </header>

        <div
          role="tablist"
          aria-label="Create kind"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: 6,
            padding: "0 22px 14px",
          }}
        >
          {(Object.keys(META) as CreateKind[]).map((k) => {
            const m = META[k];
            const active = k === kind;
            return (
              <button
                key={k}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => onKindChange(k)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "8px 10px",
                  border: `1px solid ${active ? m.color : DC.borderStrong}`,
                  background: active ? m.tint : DC.surface,
                  color: active ? m.color : DC.greyDeep,
                  borderRadius: 8,
                  cursor: "pointer",
                  font: "700 11px/1 var(--bsp-font-sans)",
                  letterSpacing: "0.04em",
                }}
              >
                <span style={{ display: "inline-flex" }}>{m.icon}</span>
                <span style={{ flex: 1, textAlign: "left", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {m.label}
                </span>
                <kbd
                  style={{
                    fontFamily: "var(--bsp-font-mono)",
                    fontSize: 9,
                    padding: "2px 5px",
                    background: active ? "rgba(255,255,255,0.5)" : DC.surfaceMuted,
                    border: `1px solid ${active ? m.color : DC.border}`,
                    borderRadius: 4,
                    color: "inherit",
                  }}
                >
                  {m.shortcut}
                </kbd>
              </button>
            );
          })}
        </div>

        {kind === "rock" ? (
          <RockForm seed={seed} onClose={onClose} onCreated={onCreated} />
        ) : null}
        {kind === "milestone" ? (
          <MilestoneForm seed={seed} onClose={onClose} onCreated={onCreated} />
        ) : null}
        {kind === "todo" ? (
          <TodoForm seed={seed} onClose={onClose} onCreated={onCreated} />
        ) : null}
        {kind === "opportunity" ? (
          <OpportunityForm seed={seed} onClose={onClose} onCreated={onCreated} />
        ) : null}
      </aside>
    </>
  );
}
